/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api } from "core/api";
import { Button, Card, FieldGroup, Form, Select, Switch } from "core/components";
import { InventoryItemFormData, ItemSupplierSchema, SupplierFormData } from "core/schema";
import { add_supplier_admin_inventory_item_path, edit_supplier_admin_inventory_item_path } from "routes";
import { IMaskInput } from "react-imask";

type InventoryItemSupplierFormProps = {
  inventory_item: InventoryItemFormData;
  suppliers: Array<SupplierFormData>;
  credit_terms: Array<Record<string, unknown>>;
};

const InventoryItemSupplierForm = ({
  show,
  onClose,
  onSave,
  suppliers,
  inventory_item,
  credit_terms,
  currentItemSupplier
}: InventoryItemSupplierFormProps): JSX.Element => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: { start_date: Date.now(), max_order_quantity: 0, preferred: false },
    resolver: yupResolver(ItemSupplierSchema)
  });
  const [selectedSupplier, setSelectedSupplier] = React.useState(null);

  React.useEffect(() => {
    if (currentItemSupplier?.id) {
      setSelectedSupplier(suppliers.find((supplier) => supplier.id == currentItemSupplier.supplier.id));
      const values = {
        ...getValues(),
        ...currentItemSupplier,
        supplier_id: currentItemSupplier.supplier.id,
        credit_term_id: currentItemSupplier.credit_term.id
      };
      reset(values);
    } else {
      setSelectedSupplier(null);
      reset(
        { preferred: false, supplier_id: "", current_price: "", credit_term_id: "", lead_time: "", min_order_quantity: "" },
        { keepDefaultValues: true, keepErrors: false }
      );
    }
  }, [show]);

  const save = async (data) => {
    try {
      if (data.id) {
        const response = await api.put(edit_supplier_admin_inventory_item_path({ id: inventory_item.id }), { item_supplier: data });
        onSave({ ...response.data, supplier: selectedSupplier });
      } else {
        const response = await api.post(add_supplier_admin_inventory_item_path({ id: inventory_item.id }), { item_supplier: data });
        onSave({ ...response.data, supplier: selectedSupplier });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <React.StrictMode>
      <Modal size="lg" centered show={show}>
        <Modal.Header>{`${currentItemSupplier?.id ? "Edit" : "Add"} Supplier`}</Modal.Header>
        <Modal.Body>
          <Form>
            <Card.Body>
              <Container>
                <Row>
                  <Col>
                    <Controller
                      name="preferred"
                      control={control}
                      render={({ field }) => (
                        <Switch {...field} label="Preferred" info="Preferred or Not" checked={field.value} value={field.value?.toString()} />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Controller
                      name="supplier_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Supplier"
                          placeholder="Select a Supplier"
                          required
                          disabled={!!currentItemSupplier?.id}
                          noOptionsMessage={() => "No available suppliers."}
                          options={suppliers}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          getOptionValue={(option) => option.id}
                          isOptionDisabled={(option) => !option.active}
                          onChange={(option) => {
                            field.onChange(option?.id);
                            setSelectedSupplier(option);
                          }}
                          ref={field.ref}
                          value={suppliers.find((supplier) => supplier.id == field.value) || ""}
                          errorMessage={errors?.supplier_id?.message}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Controller
                      name="current_price"
                      control={control}
                      render={({ field }) => (
                        <FieldGroup label="Current Price" required errorMessage={errors?.current_price?.message}>
                          <div className="d-flex">
                            <span
                              className="field"
                              style={{
                                flex: 1,
                                alignItems: "center",
                                fontWeight: "bold",
                                backgroundColor: "#e9ecef",
                                border: "1px solid #ced4da",
                                whiteSpace: "nowrap",
                                paddingTop: "10px",
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0
                              }}
                            >
                              ₱
                            </span>
                            <IMaskInput
                              mask={Number}
                              padFractionalZeros={true}
                              thousandsSeparator=","
                              radix="."
                              value={field.value?.toString()}
                              unmask={true}
                              ref={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              className="field"
                              placeholder="1000.00"
                              min={0}
                              max={9999999}
                            />
                          </div>
                        </FieldGroup>
                      )}
                    />
                    <Row>
                      <Controller
                        name="lead_time"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Lead Time (Days)" required errorMessage={errors?.lead_time?.message}>
                            <IMaskInput
                              mask={Number}
                              padFractionalZeros={true}
                              thousandsSeparator=","
                              radix="."
                              value={field.value?.toString()}
                              unmask={true}
                              inputRef={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              className="field"
                              placeholder="7"
                              min={0}
                              max={9999999}
                            />
                          </FieldGroup>
                        )}
                      />
                    </Row>
                  </Col>
                  <Col>
                    <Row>
                      <Controller
                        name="credit_term_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label="Credit Term"
                            placeholder="Select a Credit Term"
                            required
                            noOptionsMessage={() => "No available credit terms."}
                            options={credit_terms}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            onChange={(option) => {
                              field.onChange(option?.id);
                            }}
                            ref={field.ref}
                            value={credit_terms.find((credit_term) => credit_term.id == field.value) || ""}
                            errorMessage={errors?.credit_term_id?.message}
                          />
                        )}
                      />
                    </Row>
                    <Row>
                      <Controller
                        name="min_order_quantity"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Minimum Order Quantity" required errorMessage={errors?.min_order_quantity?.message}>
                            <IMaskInput
                              mask={Number}
                              padFractionalZeros={true}
                              thousandsSeparator=","
                              radix="."
                              value={field.value?.toString()}
                              unmask={true}
                              ref={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              className="field"
                              placeholder="1000.00"
                              min={0}
                              max={9999999}
                            />
                          </FieldGroup>
                        )}
                      />
                    </Row>
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              onClose();
            }}
            icon="times"
            title="Close"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Save"
            onClick={handleSubmit(
              async (data) => await save(data),
              (data) => console.log(data)
            )}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default InventoryItemSupplierForm;
